import { definedMap } from "@glideapps/ts-necessities";

import { PropertyTableGetter, PropertyDescriptor, ComponentBuilder } from "./components";
import { EnumPropertyHandler } from "./components/property-handlers";
import { Description, PropertyDescription, TableGlideType, MutatingScreenKind } from "./description";
import { PropertySection } from "./components/property-sections";
import { SerializedComponent } from "../components/SerializedApp";
import { getColumnProperty } from "./components/property-utils";
import { Corners } from "./components/image-types";
import { makeFavoriteAction, makeText } from "./builder-utils";
import { TextSize } from "./components/text-types";
import { makeTextPropertyDescriptor } from "./components/descriptor-utils";
import { isFavoritedColumnName } from "../google-sheets";

export interface OverlayContentDescription {
    readonly tagOverlayText: PropertyDescription | undefined;
    readonly avatarImage: PropertyDescription | undefined;
    readonly avatarCaption: PropertyDescription | undefined;
    readonly buttonOverlay: PropertyDescription | undefined;
}

export function defaultOverlayComponent() {
    return {
        tagOverlayText: undefined,
        avatarImage: undefined,
        avatarCaption: undefined,
        buttonOverlay: undefined,
    };
}

export enum ButtonOverlay {
    None = "none",
    Favorite = "favorite",
}

export function hasFavoriteButton<T extends Description>(
    desc: T,
    overlaysAllowed: ((desc: T) => boolean) | undefined
): boolean {
    if (overlaysAllowed?.(desc) === false) return false;
    return buttonOverlayPropertyHandler.getEnum(desc) === ButtonOverlay.Favorite;
}

export const buttonOverlayPropertyHandler = new EnumPropertyHandler(
    { buttonOverlay: ButtonOverlay.None },
    "Button",
    "Button",
    [
        {
            value: ButtonOverlay.None,
            label: "—",
        },
        {
            value: ButtonOverlay.Favorite,
            label: "Favorite",
            icon: "componentFavorite",
            columnWritten: isFavoritedColumnName,
        },
    ],
    PropertySection.Overlays,
    "small-images"
);

export function getOverlayComponents(
    desc: Description & OverlayContentDescription,
    builder: ComponentBuilder,
    table: TableGlideType,
    mutatingScreenKind: MutatingScreenKind | undefined,
    preferAvatarRight: boolean,
    tagCorners: Corners,
    textSize: TextSize
): (SerializedComponent | undefined)[] {
    const b = builder.b;

    const tagOverlayText = makeText(builder, desc.tagOverlayText, table, true);
    const topLeftOverlay = definedMap(tagOverlayText, t =>
        b.ui("TagOverlay", {
            text: t,
            corners: b.constant(tagCorners),
        })
    );

    let bottomLeftOverlay: SerializedComponent | undefined;
    let bottomRightOverlay: SerializedComponent | undefined;
    const avatarImage = makeText(builder, desc.avatarImage, table, true, false);
    const avatarCaption = makeText(builder, desc.avatarCaption, table, true);
    if (avatarImage !== undefined || avatarCaption !== undefined) {
        const avatarOverlay = b.avatarOverlay(avatarImage, avatarCaption, b.constant(textSize));
        if (preferAvatarRight) {
            bottomRightOverlay = avatarOverlay;
        } else {
            bottomLeftOverlay = avatarOverlay;
        }
    }

    let topRightOverlay: SerializedComponent | undefined;
    const button = buttonOverlayPropertyHandler.getEnum(desc);
    if (button === ButtonOverlay.Favorite && table !== undefined) {
        const favoriteAction = makeFavoriteAction(b, table, mutatingScreenKind, undefined);
        if (favoriteAction !== undefined) {
            const { lets, value, onChange: onChangeFavorite } = favoriteAction;
            topRightOverlay = b.let(
                lets,
                b.ui("ToggleIconOverlay", {
                    value,
                    onChange: onChangeFavorite,
                })
            );
        }
    }

    return [topLeftOverlay, topRightOverlay, bottomLeftOverlay, bottomRightOverlay];
}

export function getOverlayPropertyHandlers<T extends Description>(
    getIndirectTable: PropertyTableGetter<T> | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    captionLabel: string = "Caption"
): readonly PropertyDescriptor[] {
    return [
        buttonOverlayPropertyHandler.withGetIndirectTable(getIndirectTable as PropertyTableGetter<Description>),
        makeTextPropertyDescriptor("tagOverlayText", "Tag", "Enter tag", false, mutatingScreenKind, {
            emptyByDefault: true,
            propertySection: PropertySection.Overlays,
            preferredNames: ["tag"],
            getIndirectTable,
        }),
        makeTextPropertyDescriptor("avatarImage", "Avatar", "Enter URL", false, mutatingScreenKind, {
            emptyByDefault: true,
            propertySection: PropertySection.Overlays,
            preferredNames: ["avatar", "photo", "image", "picture", "pic"],
            preferredType: "image-uri",
            searchable: false,
            getIndirectTable,
        }),
        makeTextPropertyDescriptor("avatarCaption", captionLabel, "Enter avatar text", false, mutatingScreenKind, {
            emptyByDefault: true,
            propertySection: PropertySection.Overlays,
            preferredNames: ["caption", "description"],
            getIndirectTable,
        }),
    ];
}

export function getOverlaySearchProperties(desc: OverlayContentDescription): readonly string[] {
    const properties: string[] = [];

    const tag = getColumnProperty(desc.tagOverlayText);
    if (tag !== undefined) {
        properties.push(tag);
    }

    const avatarCaption = getColumnProperty(desc.avatarCaption);
    if (avatarCaption !== undefined) {
        properties.push(avatarCaption);
    }

    return properties;
}
